import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import CompetitionResultTable from '../../../components/tables/CompetitionResultTable';
import { useCompetitionResultSearch, useDeleteCompetitionResult } from '../../../hooks/useCompetitionResultApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { CompetitionResultDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const CompetitionResultList: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState<CompetitionResultDTO[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { search } = useCompetitionResultSearch();
  const deleteCompetitionResult = useDeleteCompetitionResult();

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Competition Results', href: '#' },
  ];

  const totalPages = Math.ceil(totalRecords / pageSize);

  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const response = await search({
        page: currentPage,
        pageSize,
        sortBy: 'CreatedUtc',
        sortDescending: true,
        filters: {},
      });
      setItems(response.results);
      setTotalRecords(response.totalCount);
    } catch (error) {
      console.error('Error fetching Competition Results:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [currentPage, pageSize]);

  const handleDelete = async (dto: CompetitionResultDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await deleteCompetitionResult.mutateAsync(dto.id || '');
      await fetchItems();
    } catch (error) {
      console.error('Error deleting CompetitionResult:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/competitionresults"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Manage.CompetitionResult.View"
    >
      <Card
        title="Competition Results"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton
            label="Create Results"
            onClick={() => navigate('/admin/competitionresults/create')}
          />
        }
      >
        <CompetitionResultTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/competitionresults/${dto.id}/exercises`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default CompetitionResultList;
